'use client'
import { Grid } from '@achieve/ascend'
import Section from 'components/Section'
import { _get } from 'utils/shared'
import { contentMap } from 'utils/contentful'
import { Typography } from 'components/Contentful'
import { AchieveLink } from 'components/AchieveLink'
import { useAppRouter } from 'hooks/useAppRouter/useAppRouter'
import { useEffect, useReducer, useState } from 'react'
import { ChevronDown, ChevronUp } from 'react-feather'
import { normalizeContent } from 'utils/shared/hacks'

function NavArticlesSection({ content }) {
  const sectionVariationObject = {
    ...content?.fields?.experiment,
    variant: content?.fields?.experimentVariation,
  }
  const { fullAsPath } = useAppRouter()

  const reducerFunction = (state, action) => {
    switch (action.type) {
      case 'setData':
        return { ...state, ...action.payload }
      default:
        return state
    }
  }

  const readContentData = (content) => {
    if (!content) return {}
    const navData = _get(content, 'fields.navArticlesData.sectionContentsCollection.items')
    if (navData) {
      let title, copy
      let itemActive = null
      const itemList = navData
        ?.filter((item) => item.__typename === 'PageSection')
        .map((item, index) => {
          let { title, copy, link } = contentMap(item.sectionContentsCollection.items, [
            'title',
            'copy',
            'link',
          ])
          if (fullAsPath.split('?')?.[0] === link?.linkHref) itemActive = index
          return { title, copy, link, itemActive }
        })

      if (itemActive) {
        title = itemList[itemActive].title
        copy = itemList[itemActive].copy
      }

      if (!title && !copy) {
        const dataItem = contentMap(navData, ['title', 'copy'])
        title = dataItem.title
        copy = dataItem.copy
      }

      return {
        title: title,
        copy: copy,
        itemActive,
        list: itemList,
      }
    } else {
      let { title, copy } = contentMap(normalizeContent(content?.fields?.sectionContents), [
        'title',
        'copy',
      ])
      return {
        title,
        copy,
        list: [],
      }
    }
  }
  const data = readContentData(content)

  const [{ navOpen, itemActive, title, copy, list }, dispatch] = useReducer(reducerFunction, {
    navOpen: true,
    itemActive: data?.itemActive,
    title: data?.title,
    copy: data?.copy,
    list: data?.list ?? [],
  })

  const [hideSubtitle, setHideSubtitle] = useState(false)
  useEffect(() => {
    const handleScroll = () => {
      if (list?.length > 0) {
        setHideSubtitle(window.scrollY > 0)
        // if (navOpen) setNavOpen(!window.scrollY > 0)
      }
    }
    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [list?.length, navOpen])

  const setNavOpen = (navOpen) => [
    dispatch({
      type: 'setData',
      payload: {
        navOpen,
      },
    }),
  ]

  const showSubnav = list?.length > 0 && (itemActive || _get(content, 'fields.allPage') === true)
  return (
    <Section
      contain={false}
      className={
        'bg-white z-2 data-[sticky=true]:sticky data-[sticky=true]:top-[53px] lg:data-[sticky=true]:top-[63px]'
      }
      data-sticky={list?.length > 0}
      data-testid="nav-articles-section"
    >
      {(title || copy) && (
        <Grid
          container
          className={
            'text-center justify-center px-2 py-4 lg:px-4 lg:py-8 [background:linear-gradient(180.32deg,#e3ebfe_0.28%,#f9f9fe_127.95%)]'
          }
        >
          <Grid size={{ xs: 12 }} className={'flex flex-row gap-2 justify-center'}>
            {title && (
              <Typography
                content={title?.textContent}
                variant={'headingMd'}
                data-testid="nav-articles-title"
              />
            )}
            {showSubnav && !hideSubtitle && (
              <>
                {navOpen ? (
                  <ChevronUp
                    className={'text-blue-300 cursor-pointer'}
                    size={24}
                    onClick={() => setNavOpen(!navOpen)}
                    data-testid="nav-articles-chevron-up"
                  />
                ) : (
                  <ChevronDown
                    className={'text-blue-300 cursor-pointer'}
                    size={24}
                    onClick={() => setNavOpen(!navOpen)}
                    data-testid="nav-articles-chevron-down"
                  />
                )}
              </>
            )}
          </Grid>
          {!hideSubtitle && copy && (
            <Grid size={{ xs: 12 }} marginTop={1} className={''}>
              <Typography
                content={copy?.textContent}
                variant={'bodySm'}
                data-testid="nav-articles-subtitle"
              />
            </Grid>
          )}
        </Grid>
      )}
      {showSubnav && !hideSubtitle && (
        <div
          className={
            'flex flex-col gap-2 justify-center transition-all duration-500 ease-[ease-in] overflow-hidden px-2 py-4 border-b-[0.5px] border-b-[#10182817] border-solid lg:flex-row lg:gap-8 lg:max-h-[100px] bg-neutral-25 data-[open=true]:max-h-0 data-[open=true]:p-0 lg:bg-white'
          }
          data-open={navOpen}
          data-testid="nav-articles-list"
        >
          {list?.map((item, index) => {
            const title = _get(item, 'title.textContent.json.content[0].content[0].value')
            const link = _get(item, 'link.linkHref')
            return (
              <div
                key={`item-nav-articles-${index}`}
                className={'flex justify-center'}
                data-testid={`nav-articles-item-${index}`}
              >
                <div
                  className={
                    'px-3 py-1 rounded-lg text-nowrap data-[active=true]:text-blue-400 bg-blue-50'
                  }
                  data-active={itemActive === index}
                >
                  <AchieveLink
                    href={link}
                    track={{
                      list_name: 'Breadcrumb',
                      click_text: `${title}`,
                      click_id: `${title}`,
                      click_position: '0',
                      nav_link_section: 'Breadcrumb',
                      track_event: 'page_nav_click',
                      event_action: 'link_click',
                      event_type: 'link_click',
                      sectionVariationObject,
                    }}
                    underline="hover"
                    color="inherit"
                    data-testid="nav-articles-item-link"
                  >
                    <Typography
                      content={title}
                      variant="labelSm"
                      data-testid="nav-articles-item-text"
                    />
                  </AchieveLink>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </Section>
  )
}
export { NavArticlesSection }
export default NavArticlesSection
